import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from './ui/dialog';
import { TrendingUp, TrendingDown, Plus, Trash2, RefreshCw, Zap, Loader2, DollarSign, Target, Link as LinkIcon } from 'lucide-react';
import { API } from '../App';

const SOURCES = [
  { value: 'meta', label: 'Meta (Facebook/Instagram)' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'tiktok', label: 'TikTok Ads' },
  { value: 'linkedin', label: 'LinkedIn Ads' },
  { value: 'email', label: 'Email / broadcast' },
  { value: 'manual', label: 'Manual / other' },
];

const WINDOWS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

function fmtUsd(n) {
  if (n === null || n === undefined) return '—';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function timeAgo(iso) {
  if (!iso) return 'never';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function SyncBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">never synced</span>;
  const cls = status.ok ? 'text-green-700' : 'text-red-600';
  return (
    <span className={cls}>
      {status.ok ? '✓' : '✗'} last synced {timeAgo(status.last_run_at)}
    </span>
  );
}

function RoasBadge({ roas }) {
  if (roas === null || roas === undefined) {
    return <Badge variant="outline" className="text-muted-foreground">No spend</Badge>;
  }
  if (roas >= 3) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 gap-1">
        <TrendingUp className="h-3 w-3" /> {roas.toFixed(2)}x
      </Badge>
    );
  }
  if (roas >= 1) {
    return (
      <Badge className="bg-amber-500 hover:bg-amber-600 gap-1">
        {roas.toFixed(2)}x
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-600 hover:bg-red-700 gap-1">
      <TrendingDown className="h-3 w-3" /> {roas.toFixed(2)}x
    </Badge>
  );
}

export default function CampaignRoasAdmin() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectors, setConnectors] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    utm_campaign: '',
    source: 'meta',
    amount_usd: '',
    period_start: '',
    period_end: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const [spendList, setSpendList] = useState([]);
  const [showSpendHistory, setShowSpendHistory] = useState(false);
  const [syncingMeta, setSyncingMeta] = useState(false);
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [r, c, s] = await Promise.all([
        axios.get(`${API}/admin/campaigns/roas`, { params: { days } }),
        axios.get(`${API}/admin/campaigns/connectors`),
        axios.get(`${API}/admin/campaigns/sync-status`).catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setConnectors(c.data);
      setSyncStatus(s.data);
    } catch (_e) {
      toast.error('Failed to load ROAS data');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const loadSpend = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/campaigns/spend`);
      setSpendList(r.data?.spends || []);
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showSpendHistory) loadSpend(); }, [showSpendHistory, loadSpend]);

  const submitSpend = async (e) => {
    e.preventDefault();
    if (!addForm.utm_campaign.trim() || !addForm.amount_usd || !addForm.period_start || !addForm.period_end) {
      toast.error('Campaign name, amount, and both dates are required.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/campaigns/spend`, {
        ...addForm,
        amount_usd: Number(addForm.amount_usd),
        utm_campaign: addForm.utm_campaign.trim(),
      });
      toast.success('Ad spend logged');
      setAddOpen(false);
      setAddForm({ utm_campaign: '', source: 'meta', amount_usd: '', period_start: '', period_end: '', notes: '' });
      load();
      if (showSpendHistory) loadSpend();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteSpend = async (id) => {
    if (!window.confirm('Delete this ad-spend entry?')) return;
    try {
      await axios.delete(`${API}/admin/campaigns/spend/${id}`);
      toast.success('Deleted');
      loadSpend();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const syncSource = async (source) => {
    const setter = source === 'meta' ? setSyncingMeta : setSyncingGoogle;
    const label = source === 'meta' ? 'Meta' : 'Google Ads';
    setter(true);
    try {
      const r = await axios.post(`${API}/admin/campaigns/sync-${source === 'meta' ? 'meta' : 'google-ads'}`, null, { params: { days } });
      toast.success(r.data?.message || `${label} sync complete`);
      load();
      if (showSpendHistory) loadSpend();
    } catch (err) {
      toast.error(err.response?.data?.detail || `${label} sync failed`);
    } finally {
      setter(false);
    }
  };

  const totals = data?.totals;
  const rows = data?.rows || [];

  return (
    <div className="space-y-6" data-testid="campaign-roas-admin">
      {/* Header + controls */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-heading font-medium flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" /> Campaign ROAS
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Revenue per campaign vs. ad spend. UTMs on orders drive the revenue side; log spend below to compute ROAS.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-40" data-testid="roas-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.value} value={String(w.value)}>{w.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="roas-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="roas-log-spend-btn">
                <Plus className="h-4 w-4 mr-1" /> Log ad spend
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Log ad spend</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitSpend} className="space-y-4">
                <div>
                  <label className="text-sm font-medium block mb-1">Campaign (utm_campaign) *</label>
                  <Input
                    value={addForm.utm_campaign}
                    onChange={(e) => setAddForm((f) => ({ ...f, utm_campaign: e.target.value }))}
                    placeholder="founders_story_jul1"
                    required
                    data-testid="spend-utm-campaign"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Must match the utm_campaign tag on your ad link.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium block mb-1">Source *</label>
                    <Select value={addForm.source} onValueChange={(v) => setAddForm((f) => ({ ...f, source: v }))}>
                      <SelectTrigger data-testid="spend-source">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SOURCES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Amount (USD) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={addForm.amount_usd}
                      onChange={(e) => setAddForm((f) => ({ ...f, amount_usd: e.target.value }))}
                      placeholder="250.00"
                      required
                      data-testid="spend-amount"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium block mb-1">Period start *</label>
                    <Input
                      type="date"
                      value={addForm.period_start}
                      onChange={(e) => setAddForm((f) => ({ ...f, period_start: e.target.value }))}
                      required
                      data-testid="spend-period-start"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Period end *</label>
                    <Input
                      type="date"
                      value={addForm.period_end}
                      onChange={(e) => setAddForm((f) => ({ ...f, period_end: e.target.value }))}
                      required
                      data-testid="spend-period-end"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium block mb-1">Notes (optional)</label>
                  <Textarea
                    rows={2}
                    value={addForm.notes}
                    onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                    placeholder="Meta lookalike audience, US only"
                    data-testid="spend-notes"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={saving} data-testid="spend-submit">
                    {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                    Save spend
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Auto-sync connectors banner (Option B) */}
      {connectors && (
        <Card className="border-dashed">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap" data-testid="roas-connectors">
            <Zap className="h-5 w-5 text-amber-500 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Auto-sync ad spend from Meta &amp; Google Ads · runs hourly in the background</p>
              <p className="text-xs text-muted-foreground" data-testid="roas-sync-freshness">
                Meta:{' '}
                <span className={connectors.meta.connected ? 'text-green-700 font-medium' : 'text-muted-foreground'}>
                  {connectors.meta.connected ? 'connected' : 'not connected'}
                </span>
                {connectors.meta.connected && (
                  <> {' · '}<SyncBadge status={syncStatus?.meta} /></>
                )}
                {' — '}
                Google Ads:{' '}
                <span className={connectors.google_ads.connected ? 'text-green-700 font-medium' : 'text-muted-foreground'}>
                  {connectors.google_ads.connected ? 'connected' : 'not connected'}
                </span>
                {connectors.google_ads.connected && (
                  <> {' · '}<SyncBadge status={syncStatus?.google_ads} /></>
                )}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncSource('meta')}
                disabled={!connectors.meta.connected || syncingMeta}
                data-testid="roas-sync-meta"
                title={!connectors.meta.connected ? 'Set META_ADS_* env vars first' : 'Pull latest Meta spend'}
              >
                {syncingMeta ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Sync Meta
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncSource('google_ads')}
                disabled={!connectors.google_ads.connected || syncingGoogle}
                data-testid="roas-sync-google"
                title={!connectors.google_ads.connected ? 'Set GOOGLE_ADS_* env vars first' : 'Pull latest Google Ads spend'}
              >
                {syncingGoogle ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Sync Google Ads
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="roas-total-revenue">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Total revenue</div>
            <div className="text-2xl font-medium mt-1">{fmtUsd(totals?.revenue)}</div>
            <div className="text-xs text-muted-foreground mt-1">{totals?.orders ?? 0} attributed orders</div>
          </CardContent>
        </Card>
        <Card data-testid="roas-total-spend">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Total ad spend</div>
            <div className="text-2xl font-medium mt-1">{fmtUsd(totals?.spend)}</div>
          </CardContent>
        </Card>
        <Card data-testid="roas-net-profit">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Net (rev − spend)</div>
            <div className={`text-2xl font-medium mt-1 ${(totals?.net_profit ?? 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              {fmtUsd(totals?.net_profit)}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="roas-blended">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Blended ROAS</div>
            <div className="text-2xl font-medium mt-1">
              {totals?.roas ? `${totals.roas.toFixed(2)}x` : '—'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              AOV {fmtUsd(totals?.aov)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Per-campaign table */}
      <Card>
        <CardHeader>
          <CardTitle>Per-campaign performance</CardTitle>
          <CardDescription>Sorted by revenue. ROAS &lt; 1.0x → losing money on every click.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              <Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Crunching numbers…
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground" data-testid="roas-empty">
              <Target className="h-8 w-8 mx-auto mb-3 text-primary" />
              <p className="text-sm">
                No UTM-tagged orders or spend logged in the last {days} days.
              </p>
              <p className="text-xs mt-2 max-w-md mx-auto">
                Tag your ad URLs with <code>?utm_source=&amp;utm_medium=&amp;utm_campaign=</code>, then log the spend
                to see ROAS here.
              </p>
            </div>
          ) : (
            <Table data-testid="roas-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Campaign</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">AOV</TableHead>
                  <TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                  <TableHead className="text-center">ROAS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.utm_campaign} data-testid={`roas-row-${r.utm_campaign}`}>
                    <TableCell>
                      <div className="font-medium">{r.utm_campaign}</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {r.sources.slice(0, 3).map((s) => (
                          <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{r.orders}</TableCell>
                    <TableCell className="text-right font-medium">{fmtUsd(r.revenue)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{fmtUsd(r.aov)}</TableCell>
                    <TableCell className="text-right">{fmtUsd(r.spend)}</TableCell>
                    <TableCell className={`text-right font-medium ${r.net_profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {fmtUsd(r.net_profit)}
                    </TableCell>
                    <TableCell className="text-center"><RoasBadge roas={r.roas} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Spend history */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" /> Ad spend history
            </CardTitle>
            <CardDescription>Every ad-spend entry you&rsquo;ve logged.</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowSpendHistory((s) => !s)} data-testid="roas-toggle-history">
            {showSpendHistory ? 'Hide' : 'Show'} history
          </Button>
        </CardHeader>
        {showSpendHistory && (
          <CardContent>
            {spendList.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4" data-testid="spend-history-empty">
                No spend entries yet. Click <strong>Log ad spend</strong> above to add your first one.
              </div>
            ) : (
              <Table data-testid="spend-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="w-16" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {spendList.map((s) => (
                    <TableRow key={s.spend_id}>
                      <TableCell className="font-medium">{s.utm_campaign}</TableCell>
                      <TableCell><Badge variant="outline">{s.source}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {s.period_start ? new Date(s.period_start).toLocaleDateString() : ''}
                        {' → '}
                        {s.period_end ? new Date(s.period_end).toLocaleDateString() : ''}
                      </TableCell>
                      <TableCell className="text-right">{fmtUsd(s.amount_usd)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteSpend(s.spend_id)}
                          data-testid={`spend-delete-${s.spend_id}`}
                        >
                          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>

      {/* Docs footer for Option B */}
      <Card className="bg-muted/30 border-dashed">
        <CardContent className="p-4 text-sm text-muted-foreground flex gap-3 items-start">
          <LinkIcon className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong className="text-foreground">Want auto-sync?</strong> Provide these environment variables and restart the backend:
            <div className="mt-2 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
              <div><span className="text-foreground">Meta:</span> META_ADS_ACCESS_TOKEN, META_ADS_ACCOUNT_ID</div>
              <div><span className="text-foreground">Google Ads:</span> GOOGLE_ADS_DEVELOPER_TOKEN, GOOGLE_ADS_CUSTOMER_ID, GOOGLE_ADS_REFRESH_TOKEN, GOOGLE_ADS_CLIENT_ID, GOOGLE_ADS_CLIENT_SECRET</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
